/**
 * Centralized score color logic for Comments features
 *
 * This utility provides a single source of truth for score-based color and class logic
 * used in Comments-related components (e.g., CommentScoreMetric, CommentsChannel, badges).
 *
 * Color Properties:
 * - containerBg: Background color for badges and containers
 * - textColor: Text color for the score value
 * - progressColor: Color used in progress bars/indicators
 * - className: CSS module class name for styling
 *
 * Usage:
 *   - Use getScoreColors(score) to get all relevant color CSS variables and the className for a given score.
 *   - Use getScoreLevel(score) to get the level directly from a score.
 *   - Use isHigherLevel(current, previous) to check if a level is higher than another.
 *   - Update thresholds or color variables here to affect all consumers.
 *
 * Example:
 *   import { getScoreColors, getScoreLevel, isHigherLevel } from '../util/commentScoreTheme'
 *   const { className, containerBg, textColor } = getScoreColors(score)
 *   const level = getScoreLevel(score)
 *   <Badge className={styles[className]} style={{ backgroundColor: containerBg, color: textColor }}>{score}</Badge>
 *
 * This ensures maintainability and consistency across the codebase.
 */

export const SCORE_LEVELS = {
  LOW: 'low',
  MID_LOW: 'midLow',
  MEDIUM: 'medium',
  MID_HIGH: 'midHigh',
  HIGH: 'high',
} as const

export type ScoreLevel = (typeof SCORE_LEVELS)[keyof typeof SCORE_LEVELS]

// Define the order of levels from lowest to highest
export const SCORE_LEVEL_ORDER = [
  SCORE_LEVELS.LOW,
  SCORE_LEVELS.MID_LOW,
  SCORE_LEVELS.MEDIUM,
  SCORE_LEVELS.MID_HIGH,
  SCORE_LEVELS.HIGH,
] as const

// Thresholds are defined as the maximum score for each level
export const SCORE_THRESHOLDS = {
  [SCORE_LEVELS.LOW]: 20, // 0-20: low
  [SCORE_LEVELS.MID_LOW]: 40, // 21-40: midLow
  [SCORE_LEVELS.MEDIUM]: 60, // 41-60: medium
  [SCORE_LEVELS.MID_HIGH]: 80, // 61-80: midHigh
  [SCORE_LEVELS.HIGH]: 100, // 81-100: high
} as const

type ScoreColorTheme = {
  containerBg: string
  textColor: string
  progressColor: string
  className: string
}

export const SCORE_COLOR_VARS: Record<ScoreLevel, ScoreColorTheme> = {
  [SCORE_LEVELS.LOW]: {
    containerBg: 'var(--global-color-red-100)',
    textColor: 'var(--global-color-red-700)',
    progressColor: 'var(--global-color-red-500)',
    className: 'scoreLow',
  },
  [SCORE_LEVELS.MID_LOW]: {
    containerBg: 'var(--global-color-orange-100)',
    textColor: 'var(--global-color-orange-700)',
    progressColor: 'var(--global-color-orange-500)',
    className: 'scoreMidLow',
  },
  [SCORE_LEVELS.MEDIUM]: {
    containerBg: 'var(--global-color-yellow-100)',
    textColor: 'var(--global-color-yellow-700)',
    progressColor: 'var(--global-color-yellow-500)',
    className: 'scoreMedium',
  },
  [SCORE_LEVELS.MID_HIGH]: {
    containerBg: 'var(--score-mid-high-bg)',
    textColor: 'var(--score-mid-high-text)',
    progressColor: 'var(--score-mid-high-progress)',
    className: 'scoreMidHigh',
  },
  [SCORE_LEVELS.HIGH]: {
    containerBg: 'var(--global-color-green-100)',
    textColor: 'var(--global-color-green-700)',
    progressColor: 'var(--global-color-green-500)',
    className: 'scoreHigh',
  },
} as const

export type ScoreColorKey = keyof typeof SCORE_COLOR_VARS

/**
 * Gets the score level directly from a score value
 * @param score - The score value to evaluate
 * @returns The corresponding score level
 */
export function getScoreLevel(score: number): ScoreLevel {
  if (score > SCORE_THRESHOLDS[SCORE_LEVELS.MID_HIGH]) return SCORE_LEVELS.HIGH
  if (score > SCORE_THRESHOLDS[SCORE_LEVELS.MEDIUM])
    return SCORE_LEVELS.MID_HIGH
  if (score > SCORE_THRESHOLDS[SCORE_LEVELS.MID_LOW]) return SCORE_LEVELS.MEDIUM
  if (score > SCORE_THRESHOLDS[SCORE_LEVELS.LOW]) return SCORE_LEVELS.MID_LOW
  return SCORE_LEVELS.LOW
}

/**
 * Checks if the current level is higher than the previous level
 * @param current - The current level to check
 * @param previous - The previous level to compare against
 * @returns True if the current level is higher than the previous level
 */
export function isHigherLevel(
  current: ScoreLevel,
  previous: ScoreLevel,
): boolean {
  const currentIndex = SCORE_LEVEL_ORDER.indexOf(current)
  const previousIndex = SCORE_LEVEL_ORDER.indexOf(previous)
  return currentIndex > previousIndex
}

/**
 * Gets the color variables and class name for a given score
 * @param score - The score value to get colors for
 * @returns Object containing color variables and class name
 */
export function getScoreColors(
  score: number,
): (typeof SCORE_COLOR_VARS)[ScoreLevel] {
  const key = getScoreLevel(score)
  return SCORE_COLOR_VARS[key]
}
